/**
 * ModelCard Component
 * Module 07 - Dashboard UI
 * Section 05 - Models Management (Step 5.3)
 * 
 * Individual model card displaying thumbnail, metadata, and actions
 */

import React from 'react';
import { Box, Trash2, Eye } from 'lucide-react';
import { Model } from '../../types';
import { formatBytes, formatDate, calculateCompressionRatio } from '../../utils';
import StatusBadge from '../common/StatusBadge';
import Button from '../common/Button';
import InlineEditableName from '../common/InlineEditableName';

interface ModelCardProps {
  model: Model;
  onDelete: (id: string) => void;
  onView: (model: Model) => void;
  onRename?: (id: string, name: string) => Promise<void>;
}

/**
 * Model card component with thumbnail and actions
 * 
 * @param model - Model data to display
 * @param onDelete - Callback when delete button clicked
 * @param onView - Callback when view button clicked
 * 
 * @example
 * <ModelCard 
 *   model={model} 
 *   onDelete={handleDelete} 
 *   onView={handleView} 
 * />
 */
const ModelCard: React.FC<ModelCardProps> = ({ model, onDelete, onView, onRename }) => {
  return (
    <div className="card hover:shadow-md transition-shadow duration-200 animate-fade-in">
      {/* Thumbnail */}
      <div className="relative aspect-square mb-4 bg-gray-100 rounded-lg overflow-hidden">
        {model.thumbnailUrl ? (
          <img
            src={model.thumbnailUrl}
            alt={model.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback if thumbnail fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Box className="h-16 w-16 text-gray-400" />
          </div>
        )}
        
        <div className="absolute top-2 right-2">
          <StatusBadge status={model.status} size="sm" />
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2">
        <InlineEditableName
          name={model.name}
          onSave={(newName) => onRename?.(model.id, newName) ?? Promise.resolve()}
          disabled={!onRename}
        />
        
        <div className="text-sm text-gray-500 space-y-1">
          <p>Size: {formatBytes(model.originalFileSize)}</p>
          
          {model.optimisedFileSize && (
            <p className="text-green-600">
              Optimised: {formatBytes(model.optimisedFileSize)} (
              {calculateCompressionRatio(model.originalFileSize, model.optimisedFileSize)}% smaller)
            </p>
          )}
          
          <p>Uploaded: {formatDate(model.createdAt)}</p>
        </div>

        {/* Error message for failed processing */}
        {model.status === 'failed' && model.processingError && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {model.processingError}
          </div>
        )}

        {/* Processing message */}
        {model.status === 'processing' && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            Processing... This may take a few minutes.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="flex-1"
          onClick={() => onView(model)}
          disabled={model.status !== 'ready'}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
        
        <Button
          variant="danger"
          size="sm"
          onClick={() => onDelete(model.id)}
          title="Delete model"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ModelCard;
